import axiosInstance from "./axiosInterceptor";
import {
    convertBooleanToString,
    transformLoadCarriers,
    transformLoadPersonIds,
    transformLoadVehicles,
    transformLoadContainerNumbers,
    transformLoadDrivers,
    transformLoadingBays,
    transformLoadLoadingBays,
} from "./utils/dataTransformers";
import {AxiosRequestConfig} from "axios";

const ctcLanguage = (window as any).__CTC_LANG__;

// Set Accept-Language header globally for all requests
if( ctcLanguage ) {
    axiosInstance.defaults.headers.common['Accept-Language'] = ctcLanguage;
}

// eslint-disable-next-line require-jsdoc
export const updateDriveAccessControlLane = (value, config = {}) =>
    axiosInstance.put("/flow/api/v1/user/preferences", {
        driveAccessControlLane: {
            userValue: value,
        },
        config,
    });

export const getDriveDetails = (driveId, config = {}) =>
    axiosInstance.get(
        `/flow/api/v1/drive/driveDetails?driveId=${driveId}`,
        config,
    );

export const getDriveHistoryForDriveHeader = (driveId, config = {}) =>
    axiosInstance.get(
        `/flow/api/v1/drive/driveDetails/driveHistory?driveId=${driveId}`,
        config,
    );

export const updateDriveTotalWeight = (driveId, weight, config = {}) =>
    axiosInstance.put(
        `/flow/api/v1/drive/${driveId}/updateTotalWeight`,
        {
            weight,
        },
        config,
    );

export const updateNextLoadingBay = ({ driveId, loadingId, config = {} }) =>
    axiosInstance.put(
        `/flow/api/v1/drive/updateNextLoadingBay?driveId=${driveId}&loadingId=${loadingId}`,
        {},
        config,
    );

export const updateDriveDecoupledTraffic = ({
    driveId,
    decoupleValue,
    config = {},
}) => {
    const URL = `/flow/api/v1/drive/${driveId}/decoupledTraffic`;
    return axiosInstance.put(URL, { value: decoupleValue }, config);
};

export const getInspectionOpenChecks = (driveId, config = {}) =>
    axiosInstance.get(`/flow/api/v1/inspection/${driveId}/checks`, config);

export const postDecoupleVehicleSet = ({ driveId, data, config = {} }) => {
    const URL = `/flow/api/v1/decoupled-traffic/drives/${driveId}/decoupled`;
    return axiosInstance.post(URL, data, config);
};

export const postCoupleVehicleSet = ({ driveId, dobId, config = {} }) => {
    const URL = `/flow/api/v1/decoupled-traffic/drives/${driveId}/coupled/${dobId}`;
    return axiosInstance.put(URL, { id: dobId }, config);
};

export const getEnableCheckForDecoupleVehicleSet = ({
    driveId,
    selectedComponentIDs,
    config = {},
}) => {
    const selectedComponentIDsParams = new URLSearchParams();
    selectedComponentIDs.forEach((id) =>
        selectedComponentIDsParams.append("selectedComponentIDs", id),
    );

    return axiosInstance.get(
        `/flow/api/v1/decoupled-traffic/drives/${driveId}/actionAvailability/decoupling`,
        {
            params: selectedComponentIDsParams,
            ...config,
        },
    );
};

export const getEnableCheckForCoupleVehicleSet = ({
    driveId,
    dobId,
    config = {},
}) => {
    const dobIdParams = new URLSearchParams();
    dobIdParams.append("dobId", dobId);
    const URL = `/flow/api/v1/decoupled-traffic/drives/${driveId}/actionAvailability/coupling`;

    return axiosInstance.get(URL, {
        params: dobIdParams,
        ...config,
    });
};

export const controlDrive = ({ driveId, command, config = {} }) =>
    axiosInstance.post(
        `/flow/api/v1/inspection/controlDrive/${driveId}`,
        {
            command,
        },
        config,
    );

export const getDriveControls = (driveId, config = {}) =>
    axiosInstance.get(
        `/flow/api/v1/drive/${driveId}/availableControls`,
        config,
    );

export const getLaneAccessControlInfo = (laneId, config = {}) =>
    axiosInstance.get(`/flow/api/v1/lane/accessControlInfo/${laneId}`, config);

export const accessDriveScan = ({
    driveId,
    actionName,
    comment,
    config = {},
}) =>
    axiosInstance.put(
        `/flow/api/v1/driveAccessControlWorkflow/accessDriveScan/${driveId}`,
        {
            actionName,
            ...(comment ? { comment } : {}),
        },
        config,
    );

export const getRailDriveHeader = (id, config = {}) =>
    axiosInstance.get(`/flow/api/v1/rail/weighing/headerData/${id}`, config);

export const getWorkflowButtonStates = (driveId, config = {}) =>
    axiosInstance.get(`/flow/api/v1/workflow/${driveId}/buttonStates`, config);

export const getLaneDriveId = (laneId, config = {}) =>
    axiosInstance.get(`/flow/api/v1/lane/${laneId}/driveId`, config);

export const putDriveToLane = ({ driveId, laneId, config = {} }) =>
    axiosInstance.put(
        `/flow/api/drive/putDriveOnEntryOrExitLane?driveId=${driveId}&laneId=${laneId}`,
        {},
        config,
    );

export const selectDriveAccessControlLane = (laneId, config = {}) =>
    axiosInstance.put(
        `/flow/api/v1/lane/selectDriveAccessControlLane/${laneId}`,
        {},
        config,
    );

export const getInspectionDriveRelatedData = (driveId, config = {}) =>
    axiosInstance.get(
        `/flow/api/v1/inspection/driveDetails/${driveId}`,
        config,
    );

export const getLoadingsForDrive = ({
    driveId,
    loadingActions,
    config = {},
}) => {
    const loadingActionParams = new URLSearchParams();
    loadingActions.forEach((action) =>
        loadingActionParams.append("actionNames", action),
    );

    return axiosInstance.get(`/flow/api/v1/drive/${driveId}/loadings`, {
        params: loadingActionParams,
        ...config,
    });
};

export const triggerLoadingScan = ({
    driveId,
    loadingId,
    actionName,
    config = {},
}) =>
    axiosInstance.put(
        `/flow/api/v1/drive/${driveId}/triggerLoadingScan`,
        {
            loadingId: loadingId,
            actionName: actionName,
        },
        config,
    );

export const deleteSearchTemplate = (id) =>
    axiosInstance.delete(`/flow/api/v1/searchTemplate/delete/${id}`);

export const fetchSearchTemplates = (type) =>
    axiosInstance.get(`/flow/api/v1/searchTemplate/getSearchTemplates/${type}`);

export const saveSearchTemplate = (template) =>
    axiosInstance.post(`/flow/api/v1/searchTemplate/save`, template);

export const getParkingLocations = (searchText: string) => {
    const params = new URLSearchParams();
    params.append("q", searchText);
    return axiosInstance.get(
        "/flow/api/v1/decoupled-traffic/options/parking-location",
        {
            params,
        },
    );
};

export const changeParkingLocations = (
    dobId,
    selectedParkingLocationID,
    config = {},
) =>
    axiosInstance.put(
        `/flow/api/v1/decoupled-traffic/dobs/${dobId}/parkingLocation`,
        { selectedParkingLocationID },
        config,
    );

export const getAllVehicleType = () => {
    return axiosInstance.get("/flow/api/v1/vehicles/types");
};

export const getDobStatusOptions = () => {
    return axiosInstance.get(
        "/flow/api/v1/decoupled-traffic/options/dob-status",
    );
};

export const getComponentOptions = (searchText: string, coupleableOnly) => {
    const params = new URLSearchParams();
    params.append("q", searchText);
    params.append("coupleableOnly", coupleableOnly);
    return axiosInstance
        .get(
            `/flow/api/v1/decoupled-traffic/options/decoupled-vehicle-component`,
            { params },
        )
        .then((response) => response.data);
};

export const getDecoupledTrafficPageData = (driveId) => {
    return axiosInstance.get(
        `/flow/api/v1/decoupled-traffic/drives/${driveId}`,
    );
};

export const postDobSearch = (searchData) => {
    const URL = "/flow/api/v1/dob/search";
    return axiosInstance.post(URL, searchData);
};

export const cancelDOBSearch = (uniqueBrowserTabId) => {
    return axiosInstance.post(`/flow/api/v1/dob/search/cancel`, {
        uniqueBrowserTabId,
    });
};

export const getWsUrl = () => {
    let protocol = "wss";
    if (location.protocol === "http:") {
        protocol = "ws";
    }
    return `${protocol}://${location.host}/flow/api/v1/ws/dobsearch`;
};

export const getManageQueueWsUrl = () => {
    let protocol = "wss";
    if (location.protocol === "http:") {
        protocol = "ws";
    }
    return `${protocol}://${location.host}/flow/api/v1/ws/queuemanagercounter`;
};

export const getDriveWsUrl = () => {
    let protocol = "wss";
    if (location.protocol === "http:") {
        protocol = "ws";
    }
    return `${protocol}://${location.host}/flow/api/v1/ws/drivesearch`;
};

export const getQueueManagerWsUrl = () => {
    let protocol = "wss";
    if (location.protocol === "http:") {
        protocol = "ws";
    }
    return `${protocol}://${location.host}/flow/api/v1/ws/queuemanagermonitor`;
};

export const updateAddDob = (driveId, dobId) => {
    const URL = `/flow/api/v1/decoupled-traffic/drives/${driveId}/readyForCoupling`;
    return axiosInstance.put(URL, {
        id: dobId,
    });
};

export const removeDob = ({ driveId, dobId, config }) => {
    const URL = `/flow/api/v1/decoupled-traffic/drives/${driveId}/readyForCoupling/${dobId}`;
    return axiosInstance.delete(URL, config);
};

export const getDobDetailsPageData = (dobId) => {
    return axiosInstance.get(`/flow/api/v1/decoupled-traffic/dobs/${dobId}`);
};

export const addShipment = (dobID) => {
    const URL = `/flow/api/v1/decoupled-traffic/dobs/${dobID}/shipments/`;
    return axiosInstance.post(URL);
};

export const postRequestInspection = (dobID) => {
    const URL = `/flow/api/v1/decoupled-traffic/dobs/${dobID}/inspections/`;
    return axiosInstance.post(URL);
};

export const saveSearchTemplates = (templates) =>
    axiosInstance.post(`/flow/api/v1/searchTemplate/save/all`, templates);

export const getPermissionDocumentationList = (params) =>
    axiosInstance.get("/flow/api/v1/permission/documentation", params);

export const getDriveCustomerOptions = () =>
    axiosInstance.get("/flow/flow-base/drive/searchCustomers.json");

export const getHistoricalMilestonesOptions = () =>
    axiosInstance.get("/flow/api/v1/historicalMilestonesForAutocomplete");

export const logon = ({ username, password }) => {
    const formData = new FormData();
    formData.append("username", username);
    formData.append("password", password);

    return axiosInstance.post(`/flow/api/v1/user/logon`, formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
};

export const loadInspectionCheckGroups = (driveId, checkListResultId) =>
    axiosInstance.get(
        `/flow/api/v1/inspection/startCheck/${driveId}/${checkListResultId}`,
    );

export const getInspectionImageThumbnail = (imageId, checkListResultId) =>
    axiosInstance
        .get(
            `/flow/api/v1/inspection/${checkListResultId}/image/thumbnail/${imageId}`,
        )
        .then(({ data }) => Promise.resolve({ imageId, data }));

export const getInspectionImage = (imageId, checkListResultId) =>
    axiosInstance.get(
        `/flow/api/v1/inspection/${checkListResultId}/image/${imageId}`,
    );

export const uploadInspectionImage = (image, driveId, checkListResultId) => {
    const bodyFormData = new FormData();
    bodyFormData.append("image", image);

    return axiosInstance.post(
        `/flow/api/v1/inspection/image/uploadPictureToCheckListResult/${driveId}/${checkListResultId}`,
        bodyFormData,
        {
            headers: { "Content-Type": "multipart/form-data" },
        },
    );
};

export const uploadInspectionSignature = (
    signature,
    driveId,
    checkListResultId,
) => {
    const bodyFormData = new FormData();
    bodyFormData.append("image", signature);

    return axiosInstance.post(
        `/flow/api/v1/inspection/image/uploadSignatureToCheckListResult/${driveId}/${checkListResultId}`,
        bodyFormData,
        {
            headers: { "Content-Type": "multipart/form-data" },
        },
    );
};

export const finishInspectionCheck = (payload, driveId, checkListResultId) =>
    axiosInstance.put(
        `/flow/api/v1/inspection/finishCheck/${driveId}/${checkListResultId}`,
        payload,
    );

export const cancelInspectionCheck = (driveId, checkListResultId) =>
    axiosInstance.get(
        `/flow/api/v1/inspection/cancelCheck/${driveId}/${checkListResultId}`,
    );

export const loadCarNumbers = (vehicleTypesIds) => (searchText) => {
    const params = new URLSearchParams();
    params.append("term", searchText);
    params.append("componentType", vehicleTypesIds);
    params.append("extended", "true");

    return axiosInstance
        .get("/flow/flow-base/masterdata/component-options.json", {
            params,
        })
        .then((response) => response.data);
};

export const loadLoadingBays = (searchText) => {
    const params = new URLSearchParams();
    params.append("term", searchText);
    params.append("active", "true");
    params.append("extended", "true");

    return axiosInstance
        .get("/flow/flow-base/masterdata/ladestelle-options.json", {
            params,
        })
        .then((response) => transformLoadingBays(response));
};

export const loadCarriers = (searchText) => {
    const params = new URLSearchParams();
    params.append("term", searchText);
    params.append("active", "true");
    params.append("spediteurRole", "true");
    params.append("extended", "true");

    return axiosInstance
        .get("/flow/flow-base/masterdata/spediteur-options.json", {
            params,
        })
        .then((response) => response.data);
};

export const loadContainerNumbers = (containerTypeIds) => (searchText) => {
    const params = new URLSearchParams();
    params.append("term", searchText);
    params.append("componentType", containerTypeIds);
    params.append("extended", "true");

    return axiosInstance
        .get("/flow/flow-base/masterdata/component-options.json", {
            params,
        })
        .then((response) => transformLoadContainerNumbers(response));
};

export const loadUnNumbers = (searchText) => {
    const params = new URLSearchParams();
    params.append("term", searchText);
    params.append("extended", "true");

    return axiosInstance
        .get("/flow/flow-base/masterdata/product/unNumber-options.json", {
            params,
        })
        .then((response) => response.data);
};

export const fetchPlacardInformation = (driveId) =>
    axiosInstance.get(`/flow/api/v1/placard/info/${driveId}`);

export const loadCurrentMilestonesForDriveSearch = () =>
    axiosInstance.get("/flow/api/v1/currentMilestonesForDriveSearch");

export const loadCarriersForAutoComplete = (
    searchText,
    active = true,
    roleFreightCarrier,
    roleCarrier,
) => {
    const params = new URLSearchParams({
        term: searchText,
        active: convertBooleanToString(active),
        frachtfuehrerRole: convertBooleanToString(roleFreightCarrier),
        spediteurRole: convertBooleanToString(roleCarrier),
        extended: "true",
    });

    return axiosInstance
        .get("/flow/flow-base/masterdata/spediteur-options.json", {
            params,
        })
        .then((response) => Promise.resolve(transformLoadCarriers(response)));
};

export const loadDriversForAutocomplete = (searchText, active = true) => {
    const params = new URLSearchParams({
        term: searchText,
        active: convertBooleanToString(active),
        extended: "true",
    });

    return axiosInstance
        .get("/flow/flow-base/masterdata/fahrer-options.json", { params })
        .then((response) => Promise.resolve(transformLoadDrivers(response)));
};

export const loadPersonIdsForAutocomplete = (searchText) => {
    const params = new URLSearchParams({
        term: searchText,
        extended: "true",
    });

    return axiosInstance
        .get("/flow/flow-base/masterdata/flowcard-options.json", { params })
        .then((response) => Promise.resolve(transformLoadPersonIds(response)));
};

export const loadVehiclesForAutocomplete = (searchText) => {
    const params = new URLSearchParams({
        term: searchText,
        extended: "true",
    });

    return axiosInstance
        .get("/flow/flow-base/masterdata/component-options.json", {
            params,
        })
        .then((response) => Promise.resolve(transformLoadVehicles(response)));
};

export const loadLoadingBaysForAutocomplete = (searchText) => {
    const params = new URLSearchParams({
        term: searchText,
        active: "true",
        extended: "true",
    });

    return axiosInstance
        .get("/flow/flow-base/masterdata/ladestelle-options.json", {
            params,
        })
        .then((response) =>
            Promise.resolve(transformLoadLoadingBays(response)),
        );
};

export const loadDrives = (payload, config: AxiosRequestConfig) => {
    return axiosInstance.post(`/flow/api/v1/drive/search`, payload, config);
};

export const cancelSearch = (uniqueBrowserTabId) => {
    return axiosInstance.post(`/flow/api/v1/drive/search/cancel`, {
        uniqueBrowserTabId: uniqueBrowserTabId,
    });
};

export const changePassword = (newPassword) => {
    return axiosInstance.put("/flow/api/v1/user/password", { newPassword });
};

export const getUserPicture = (userPictureUrl) => {
    return axiosInstance
        .get(userPictureUrl, { responseType: "blob" })
        .then((response) => response.data);
};

export const updateUserPicture = (userPreferences) => {
    return axiosInstance
        .post("/flow/api/v1/user/preferences/user-picture", userPreferences, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then((response) => response.data);
};

export const deleteUserPicture = () => {
    return axiosInstance.delete("/flow/api/v1/user/preferences/user-picture");
};

export const logout = () => {
    return axiosInstance.put("/flow/api/v1/user/logout");
};

export const updateUserPreferences = (userPreferences) => {
    return axiosInstance.put("/flow/api/v1/user/preferences", userPreferences);
};

export const getLaneOptions = (siteName: string) => {
    return axiosInstance.get(`/flow/api/v1/user/preferences/lane/options?site=${siteName}`);
};

export const loadDataByDriveId = (driveId: string) => {
    return axiosInstance
        .get(`/flow/api/v1/importantinformation/drive/${driveId}`)
        .then((res) => res.data);
};

export const loadDataByShipmentIds = (shipmentIds: string[]) => {
    const params = new URLSearchParams();
    shipmentIds.forEach(id => params.append("shipmentIds", id));

    return axiosInstance
        .get("/flow/api/v1/importantinformation/shipments", {
            params,
        })
        .then((res) => res.data);
};

export const loadImportantInfoByShipmentNumber = (shipmentNumber: string) => {
    return axiosInstance
        .get(`/flow/api/v1/importantinformation/shipment/${shipmentNumber}`)
        .then((res) => res.data);
};

export const saveDriveRemark = (driveId: string, text: string) => {
    return axiosInstance
        .post(`/flow/api/v1/drive/comment/${driveId}`, { text })
        .then((res) => res.data);
};

export const loadDriveImage = (imageId: string) => {
    return axiosInstance
        .get(`/flow/api/v1/inspection/image/drive/${imageId}`)
        .then((res) => res.data);
};

export const uploadPhoto = (driveId: string, photo: File) => {
    const bodyFormData = new FormData();
    bodyFormData.append("image", photo);

    return axiosInstance
        .post(`/flow/api/v1/inspection/image/drive/${driveId}`, bodyFormData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then((res) => res.data);
};

export const getCountersForForwarding = (qmEntryId) => {
    return axiosInstance
        .get(`/flow/api/v1/queuemanager/getSuitableActiveCounters/${qmEntryId}`)
        .then((res) => res.data);
};

export const forwardEntry = (qmEntryId, newQmCounterId) => {
    return axiosInstance
        .get(
            `/flow/api/v1/queuemanager/forwardEntry/${qmEntryId}/${newQmCounterId}`,
        )
        .then((res) => res.data);
};

export const getDriverIdForQMEntry = (qmEntryId) => {
    return axiosInstance
        .get(`/flow/api/v1/queuemanager/getDriverIdForQMEntry/${qmEntryId}`)
        .then((res) => res.data);
};

export const cancelEntry = (payload) => {
    return axiosInstance
        .post("/flow/api/v1/queuemanager/setCancelled", payload)
        .then((res) => res.data);
};

export const activateCounterApi = (payload) => {
    return axiosInstance
        .post("/flow/api/v1/queuemanager/activateCounter", payload)
        .then((res) => res.data);
};

export const deactivateCounterApi = (counterId) => {
    return axiosInstance
        .get(`/flow/api/v1/queuemanager/deactivateCounter/${counterId}`)
        .then((res) => res.data);
};

export const loadOverview = () => {
    return axiosInstance
        .get("/flow/api/v1/queuemanager/overview")
        .then((res) => res.data);
};

export const loadForLocation = (locationId) => {
    return axiosInstance
        .get("/flow/api/v1/queuemanager/overview/location/" + locationId)
        .then((res) => res.data);
};

export const getAllQueueNames = () => {
    return axiosInstance
        .get("/flow/api/v1/queuemanager/getAllQueueNames")
        .then((res) => res.data);
};

export const createRailcarNumber = (payload) => {
    return axiosInstance
        .post("/flow/api/v1/rail/wagons", payload)
        .then((res) => res.data);
};

export const createContainerNumber = (payload) => {
    return axiosInstance
        .post("/flow/api/v1/rail/containers", payload)
        .then((res) => res.data);
};

export const validateContainerNumber = (number, typeId) => {
    return axiosInstance
        .post("/flow/api/v1/rail/containers/validate", number, typeId)
        .then((res) => res.data);
};

export const loadVehicleTypes = () => {
    return axiosInstance
        .get("/flow/api/v1/rail/railVehicleTypes")
        .then((res) => res.data);
};

export const loadProcess = (driveId) => {
    return axiosInstance
        .get(`/flow/api/v1/rail/drives/${driveId}`)
        .then((res) => res.data);
};

export const saveRailProcess = (payload, driveId) => {
    const url = `/flow/api/v1/rail/drives${driveId ? `/${driveId}` : ""}`;
    const method = driveId ? "put" : "post";

    return axiosInstance[method](url, payload).then((res) => res.data);
};

export const getRailDrives = (payload) => {
    return axiosInstance
        .get("/flow/api/v1/rail/search", payload)
        .then((res) => res.data);
};

export const loadStandardLane = (driveId) => {
    return axiosInstance
        .get(`/flow/api/v1/rail/weighing/${driveId}`)
        .then((res) => res.data);
};

export const controlRailDrive = (payload) => {
    return axiosInstance
        .post("/flow/api/v1/rail/controlDrive", payload)
        .then((res) => res.data);
};

export const triggerMockError = () => {
    const response = axiosInstance.get("/flow/api/v1/mock/error");
    return response;
};

export const sendValidityUpdateRequest = (
    url,
    fieldName,
    updateAction,
    timeout = 5000,
) => {
    return axiosInstance.post(
        url,
        {
            fieldName: fieldName,
            updateAction: updateAction,
        },
        {
            timeout: timeout,
        },
    );
};

export const linkDriverToCTCWallet = (
    url: string,
    driverId: number,
    scannedQrCode: string,
) => {
    return axiosInstance.post(url, {
        driverId,
        scannedQrCode,
    });
};

export const sendQRCode = (url: string, qrCode: string, headers = {}) => {
    return axiosInstance.post(url, qrCode, { headers });
};

export const fetchFileList = (url: string) => {
    return axiosInstance.get(url);
};

export const uploadFiles = (url: string, formData: FormData, config = {}) => {
    return axiosInstance.post(url, formData, config);
};

export const deleteFile = (url: string, fileId: string) => {
    return axiosInstance.delete(`${url}/${fileId}`);
};
export const updateCallStatus = (
    url: string,
    payload: { callNotRequired: boolean; callStatus: string },
) => {
    return axiosInstance.put(url, payload);
};

export const fetchLoadingBayCheckinData = (url: string) => {
    return axiosInstance.get(url);
};

export const updateLoadingBayInfo = (url: string, info: string) => {
    return axiosInstance.put(url, info, {
        headers: { "Content-Type": "text/plain; charset=utf-8" },
    });
};

export const deleteLoadingBayInfo = (url: string) => {
    return axiosInstance.delete(url, {
        headers: { "Content-Type": "text/plain; charset=utf-8" },
    });
};

export const sendUserChoice = (
    url: string,
    choice: string,
    jsonData: object | null,
) => {
    const config = {
        headers: {
            "Content-Type": jsonData ? "application/json" : "text/plain",
        },
        timeout: 5000,
    };

    if (jsonData) {
        return axiosInstance.put(url, { ...jsonData, choice }, config);
    } else {
        return axiosInstance.post(url, choice, config);
    }
};

export const fetchEmailConfig = (url: string) => {
    return axiosInstance.get(url);
};

export const sendEmail = (
    url: string,
    payload: { recipientAddresses: string; remarkToRecipient: string },
) => {
    return axiosInstance.post(url, payload);
};

export const fetchData = (url: string, config = {}) => {
    return axiosInstance.get(url, config);
};

export const fetchThumbnailImage = (thumbnailUrl: string) => {
    return axiosInstance.get(thumbnailUrl).then(({ data }) => data);
};

export const fetchChamberData = (url: string) => {
    return axiosInstance.get(url);
};

export const updateChamberData = (url: string, data: object) => {
    return axiosInstance.put(url, data);
};
/**
 * DriverSelfService / WelcomeView data
 * @return {Promise<axios.AxiosResponse<any>>}
 */
export const fetchDriverSelfServiceWelcomeViewData = () =>
    axiosInstance.get(`/flow/api/v1/driverSelfService/welcomeView`);

export const pingFlow = (requester) => {
    const params = new URLSearchParams();
    params.append("requester", requester);

    return axiosInstance.get("/flow/api/v1/healthcheck/ping", { params: params });
};
